import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  ScrollView,
} from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { FONTS } from '../../constants/config';
import { CalendarEvent, CalendarDay } from './types';
import EventCard from './EventCard';

interface DayViewProps {
  day: CalendarDay;
  startHour: number;
  endHour: number;
  onEventPress?: (event: CalendarEvent) => void;
}

const HOUR_HEIGHT = 70;
const TIME_COLUMN_WIDTH = 52;
const MAX_VISIBLE_OVERLAP = 4;

function timeToHours(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h + m / 60;
}

interface LayoutedEvent {
  event: CalendarEvent;
  column: number;
  totalColumns: number;
  overflowGroup?: CalendarEvent[];
}

function layoutOverlappingEvents(events: CalendarEvent[]): LayoutedEvent[] {
  if (events.length === 0) return [];
  const sorted = [...events].sort((a, b) => {
    const diff = timeToHours(a.startTime) - timeToHours(b.startTime);
    return diff !== 0 ? diff : timeToHours(b.endTime) - timeToHours(a.endTime);
  });
  const clusters: CalendarEvent[][] = [];
  let currentCluster: CalendarEvent[] = [sorted[0]];
  let clusterEnd = timeToHours(sorted[0].endTime);
  for (let i = 1; i < sorted.length; i++) {
    const evStart = timeToHours(sorted[i].startTime);
    if (evStart < clusterEnd) {
      currentCluster.push(sorted[i]);
      clusterEnd = Math.max(clusterEnd, timeToHours(sorted[i].endTime));
    } else {
      clusters.push(currentCluster);
      currentCluster = [sorted[i]];
      clusterEnd = timeToHours(sorted[i].endTime);
    }
  }
  clusters.push(currentCluster);
  const result: LayoutedEvent[] = [];
  for (const cluster of clusters) {
    if (cluster.length <= MAX_VISIBLE_OVERLAP) {
      const totalColumns = cluster.length;
      cluster.forEach((ev, idx) => {
        result.push({ event: ev, column: idx, totalColumns });
      });
    } else {
      const visible = MAX_VISIBLE_OVERLAP - 1;
      const totalColumns = MAX_VISIBLE_OVERLAP;
      for (let i = 0; i < visible; i++) {
        result.push({ event: cluster[i], column: i, totalColumns });
      }
      result.push({
        event: cluster[visible],
        column: visible,
        totalColumns,
        overflowGroup: cluster.slice(visible),
      });
    }
  }
  return result;
}

function formatHourLabel(hour: number): string {
  if (hour === 0 || hour === 12) {
    return hour === 0 ? '12AM' : '12PM';
  }
  return hour < 12 ? `${hour}AM` : `${hour - 12}PM`;
}

export default function DayView({
  day,
  startHour,
  endHour,
  onEventPress,
}: DayViewProps) {
  const { colors } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [overflowModal, setOverflowModal] = useState<CalendarEvent[] | null>(null);
  const totalHours = endHour - startHour;
  const hours = Array.from({ length: totalHours + 1 }, (_, i) => startHour + i);

  return (
    <View style={styles.container}>
      {/* Day header */}
      <View style={[styles.dayHeader, { borderBottomColor: colors.border }]}>
        <Text style={[styles.dayName, { color: colors.textMuted }]}>
          {day.dayName}
        </Text>
        <View
          style={[
            styles.dayNumberWrapper,
            day.isToday && [styles.todayCircle, { backgroundColor: '#a9211f' }],
          ]}
        >
          <Text
            style={[
              styles.dayNumber,
              { color: day.isToday ? '#FFFFFF' : colors.text },
            ]}
          >
            {day.dayNumber}
          </Text>
        </View>
      </View>

      {/* Time grid */}
      <View style={styles.gridBody}>
        {/* Time labels */}
        <View style={[styles.timeColumn, { width: TIME_COLUMN_WIDTH }]}>
          {hours.map((hour) => (
            <View key={hour} style={[styles.timeLabel, { height: HOUR_HEIGHT }]}>
              <Text style={[styles.timeLabelText, { color: colors.textMuted }]}>
                {formatHourLabel(hour)}
              </Text>
            </View>
          ))}
        </View>

        {/* Event column */}
        <View style={styles.eventColumn}>
          {/* Hour lines */}
          {hours.map((hour) => (
            <View
              key={hour}
              style={[
                styles.hourCell,
                { height: HOUR_HEIGHT, borderBottomColor: colors.divider },
              ]}
            />
          ))}

          {/* Events (with overlap handling) */}
          {layoutOverlappingEvents(day.events).map((item) => {
            const eventStart = timeToHours(item.event.startTime);
            const eventEnd = timeToHours(item.event.endTime);
            const top = (eventStart - startHour) * HOUR_HEIGHT;
            const height = Math.max((eventEnd - eventStart) * HOUR_HEIGHT - 4, 24);

            const slotWidth = 100 / item.totalColumns;
            const leftPct = isRTL
              ? (item.totalColumns - item.column - 1) * slotWidth
              : item.column * slotWidth;

            if (item.overflowGroup) {
              return (
                <TouchableOpacity
                  key={`overflow-${item.event.id}`}
                  style={[
                    styles.eventWrapper,
                    {
                      top,
                      height: height + 4,
                      left: `${leftPct}%` as any,
                      right: undefined,
                      width: `${slotWidth}%` as any,
                    },
                  ]}
                  activeOpacity={0.7}
                  onPress={() => setOverflowModal(item.overflowGroup!)}
                >
                  <View style={[styles.overflowBtn, { backgroundColor: colors.cardBorder }]}>
                    <Text style={[styles.overflowText, { color: colors.textPrimary }]}>
                      +{item.overflowGroup.length}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            }

            return (
              <View
                key={item.event.id}
                style={[
                  styles.eventWrapper,
                  {
                    top,
                    height: height + 4,
                    left: `${leftPct}%` as any,
                    right: undefined,
                    width: `${slotWidth}%` as any,
                  },
                ]}
              >
                <EventCard
                  event={item.event}
                  height={height}
                  onPress={onEventPress}
                />
              </View>
            );
          })}
        </View>
      </View>

      {/* Overflow modal */}
      {overflowModal && (
        <Modal transparent animationType="fade" visible onRequestClose={() => setOverflowModal(null)}>
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={() => setOverflowModal(null)}
          >
            <View style={[styles.modalContent, { backgroundColor: colors.card }]}>
              <Text style={[styles.modalTitle, { color: colors.textPrimary }]}>
                {isRTL ? 'المزيد من الأنشطة' : 'More Activities'}
              </Text>
              <ScrollView style={{ maxHeight: 300 }}>
                {overflowModal.map((event) => (
                  <TouchableOpacity
                    key={event.id}
                    style={styles.modalEventRow}
                    onPress={() => { setOverflowModal(null); onEventPress?.(event); }}
                  >
                    <EventCard event={event} height={56} onPress={onEventPress} />
                  </TouchableOpacity>
                ))}
              </ScrollView>
              <TouchableOpacity
                style={[styles.modalCloseBtn, { backgroundColor: '#A9211F' }]}
                onPress={() => setOverflowModal(null)}
              >
                <Text style={styles.modalCloseBtnText}>{isRTL ? 'إغلاق' : 'Close'}</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </Modal>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  dayHeader: {
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    gap: 4,
  },
  dayName: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    lineHeight: 16,
  },
  dayNumber: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    lineHeight: 22,
  },
  dayNumberWrapper: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  todayCircle: {
    borderRadius: 14,
  },
  gridBody: {
    flexDirection: 'row',
  },
  timeColumn: {
    flexShrink: 0,
  },
  timeLabel: {
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  timeLabelText: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    lineHeight: 14,
    marginTop: -7,
  },
  eventColumn: {
    flex: 1,
    position: 'relative',
  },
  hourCell: {
    borderBottomWidth: 1,
  },
  eventWrapper: {
    position: 'absolute',
    left: 2,
    right: 2,
    zIndex: 1,
    padding: 2,
  },
  overflowBtn: {
    flex: 1,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    margin: 2,
  },
  overflowText: {
    fontSize: 13,
    fontFamily: FONTS.medium,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: 340,
    borderRadius: 12,
    padding: 20,
    gap: 12,
  },
  modalTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    textAlign: 'center',
  },
  modalEventRow: {
    marginBottom: 8,
  },
  modalCloseBtn: {
    borderRadius: 8,
    paddingVertical: 10,
    alignItems: 'center',
    marginTop: 4,
  },
  modalCloseBtnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: FONTS.medium,
  },
});
